import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import './styles.css';
import SEO from '../../components/SEO';
import Icon from '../../components/Icons';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.eternavaults.com';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loginWithRedirect, isLoading } = useAuth0();
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [error, setError] = useState('');
  
  // Get email from URL params, Auth0 user, or allow manual entry
  const [emailInput, setEmailInput] = useState('');
  const emailFromParams = searchParams.get('email') || user?.email || '';
  const email = emailFromParams || emailInput;
  const errorMessage = searchParams.get('error') || '';

  const handleResendVerification = async () => {
    // Get email from user object or URL params or input
    const emailToUse = user?.email || emailFromParams || emailInput;
    
    if (!emailToUse) {
      setError('Please enter your email address.');
      return;
    }

    setResending(true);
    setError('');
    setResendSuccess(false);

    try {
      // No authentication required - send email directly
      const response = await fetch(`${API_BASE_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: emailToUse }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend verification email');
      }

      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err) {
      setError(err.message || 'Failed to resend verification email. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleBackToLogin = async () => {
    // Redirect to Auth0 login instead of internal login page
    try {
      await loginWithRedirect({
        appState: { returnTo: window.location.origin }
      });
    } catch (err) {
      console.error('Failed to redirect to Auth0 login:', err);
      // Fallback to internal login page if Auth0 redirect fails
      navigate('/login');
    }
  };

  return (
    <>
      <SEO
        title="Verify Your Email - EternaVaults"
        description="Please verify your email address to access your EternaVaults account"
      />
      <div className="verify-email-container">
        <div className="verify-email-card">
          <div className="verify-email-icon">
            <Icon name="mail" size={64} />
          </div>
          
          <h1>Verify Your Email Address</h1>
          
          <p className="verify-email-description">
            {emailFromParams ? (
              <>
                We've sent a verification email to <strong>{emailFromParams}</strong>.
                Please check your inbox and click the verification link to activate your account.
              </>
            ) : user?.email ? (
              <>
                We've sent a verification email to <strong>{user.email}</strong>.
                Please check your inbox and click the verification link to activate your account.
              </>
            ) : (
              <>
                Please log in to resend your verification email.
              </>
            )}
          </p>

          {errorMessage && (
            <div className="verify-email-error">
              {errorMessage}
            </div>
          )}

          {(!emailFromParams && !user?.email) && (
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                color: '#bfa97a', 
                marginBottom: '0.5rem',
                fontSize: '0.95rem',
                fontWeight: 500
              }}>
                Email Address
              </label>
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="Enter your email address"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: 'rgba(40, 35, 29, 0.8)',
                  border: '1px solid rgba(191, 169, 122, 0.3)',
                  borderRadius: '8px',
                  color: '#e2c89c',
                  fontSize: '1rem',
                  fontFamily: 'inherit'
                }}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && emailInput) {
                    handleResendVerification();
                  }
                }}
              />
            </div>
          )}

                 <div className="verify-email-actions">
                   <button
                     onClick={handleResendVerification}
                     disabled={resending || isLoading || (!emailFromParams && !user?.email && !emailInput)}
                     className="verify-email-resend-btn"
                   >
                     {resending ? (
                       <>
                         <span className="spinner" /> Sending...
                       </>
                     ) : (
                       <>
                         <Icon name="refresh" size={18} />
                         Resend Verification Email
                       </>
                     )}
                   </button>

            {resendSuccess && (
              <div className="verify-email-success">
                ✓ Verification email sent! Please check your inbox.
              </div>
            )}

            {error && (
              <div className="verify-email-error">
                {error}
              </div>
            )}
          </div>

          <div className="verify-email-help">
            <h3>Didn't receive the email?</h3>
            <ul>
              <li>Check your spam/junk folder</li>
              <li>Make sure you entered the correct email address</li>
              <li>Wait a few minutes - emails can take up to 5 minutes to arrive</li>
              <li>Try resending the verification email using the button above</li>
            </ul>
          </div>

          <div className="verify-email-footer">
            <button
              onClick={handleBackToLogin}
              className="verify-email-back-btn"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

